// Random Lamp Generator Utility
// All debug logging is controlled by RANDOM_LAMP_DEBUG

const RANDOM_LAMP_DEBUG = true; // Set to false to disable debug logs

// Helper functions
const randomInRange = (min, max) => Math.random() * (max - min) + min;
const randomIntInRange = (min, max) => Math.floor(randomInRange(min, max));
const randomChoice = (array) => array[Math.floor(Math.random() * array.length)];

/**
 * Generate a random lamp design object.
 * @param {Object} options - Configuration and dependencies
 * @param {Object} options.LAMP_CONFIG - Lamp config constants
 * @param {Object} options.MODIFIER_CONFIGS - Modifier configs
 * @param {Object} options.MODIFIER_TYPES - Modifier types
 * @param {number} options.TARGET_FACE_SIZE - Target face size (mm)
 * @param {number} options.angleThreshold - Overhang angle threshold (degrees)
 * @param {function} options.LampGeometry - LampGeometry class (from lamp-core)
 * @param {function} options.validateLampGeometry - Geometry validation function
 * @param {function} options.generateAestheticCompliantColorPair - Color pair generator
 * @param {Object} options.defaultRenderingConfig - Default rendering config
 * @param {Object} options.renderingConfig - Current rendering config (for wireframe, etc.)
 * @param {Object} options.MODIFIER_RETRY_CONFIG - Modifier retry config
 * @returns {Promise<Object>} - Resolves to a valid lamp design object
 */
export async function generateRandomLamp({
  LAMP_CONFIG,
  MODIFIER_CONFIGS,
  MODIFIER_TYPES,
  TARGET_FACE_SIZE,
  angleThreshold,
  LampGeometry,
  validateLampGeometry,
  generateAestheticCompliantColorPair,
  defaultRenderingConfig,
  renderingConfig,
  MODIFIER_RETRY_CONFIG
}) {
  let attempts = 0;
  let validDesign = false;
  const maxAttempts = 500;
  const lampNoiseSeed = Math.random();
  if (RANDOM_LAMP_DEBUG) console.log(`[RandomLamp] Using noiseSeed: ${lampNoiseSeed}`);

  while (!validDesign && attempts < maxAttempts) {
    attempts++;
    if (RANDOM_LAMP_DEBUG) {
      console.log(`\n============================`);
      console.log(`Attempt #${attempts}`);
    }
    // Random dimensions
    const newDiameter = randomIntInRange(115, 220);
    const newHeight = randomIntInRange(150, 240);
    if (RANDOM_LAMP_DEBUG) console.log(`[RandomLamp] Chosen diameter: ${newDiameter}, height: ${newHeight}`);

    // Profile points
    const minPoints = 3;
    const maxPoints = 6;
    const numPoints = randomIntInRange(minPoints, maxPoints);
    if (RANDOM_LAMP_DEBUG) console.log(`[RandomLamp] Number of profile points: ${numPoints}`);
    let newProfilePoints = [];
    newProfilePoints.push({ x: Math.max(0.5, randomInRange(0.7, 1.0)), y: 0.0 });
    let yValues = [0.0];
    for (let i = 1; i < numPoints - 1; i++) {
      let y = i / (numPoints - 1) + randomInRange(-0.03, 0.03);
      y = Math.max(0.05, Math.min(0.95, y));
      yValues.push(y);
    }
    yValues.push(0.9);
    yValues = Array.from(new Set(yValues)).sort((a, b) => a - b);
    for (let i = 1; i < yValues.length - 1; i++) {
      let baseRadius = randomInRange(0.7, 0.95);
      if (Math.random() < 0.15) {
        baseRadius += Math.sin(yValues[i] * Math.PI * randomInRange(1, 2)) * randomInRange(0.02, 0.07);
      }
      const clampedRadius = Math.max(0.7, Math.min(1.0, baseRadius));
      newProfilePoints.push({ x: clampedRadius, y: yValues[i] });
    }
    newProfilePoints.push({ x: Math.max(0.7, randomInRange(0.7, 1.0)), y: 1.0 });
    if (Math.random() < 0.2) {
      newProfilePoints = newProfilePoints.map(pt => ({ x: 1.2 - pt.x, y: pt.y }));
    }
    if (RANDOM_LAMP_DEBUG) console.log('[RandomLamp] Profile points:', JSON.stringify(newProfilePoints));

    // Shuffle modifier types and pick how many to apply
    const modifierTypes = Object.keys(MODIFIER_CONFIGS);
    const modifierTypesCopy = [...modifierTypes];
    const shuffledOrder = modifierTypesCopy.sort(() => Math.random() - 0.5);
    const numToApply = randomIntInRange(
      LAMP_CONFIG.RANDOM_GENERATION.MODIFIERS.MIN_COUNT,
      LAMP_CONFIG.RANDOM_GENERATION.MODIFIERS.MAX_COUNT + 1
    );
    if (RANDOM_LAMP_DEBUG) {
      console.log(`[RandomLamp] Shuffled modifier order:`, shuffledOrder);
      console.log(`[RandomLamp] Number of modifiers to apply: ${numToApply}`);
    }

    let enabledModifiers = [];
    let allModifiersValid = true;
    let appliedModifierOrder = [];

    for (let i = 0; i < numToApply; i++) {
      const type = shuffledOrder[i];
      const config = MODIFIER_CONFIGS[type];
      let params = {};
      Object.entries(config.parameters).forEach(([paramName, paramConfig]) => {
        if (paramConfig.type === 'select') {
          params[paramName] = randomChoice(paramConfig.options).value;
        } else if (
          type === MODIFIER_TYPES.RADIAL_WAVE &&
          paramName === 'wavesPerRevolution'
        ) {
          params[paramName] = randomIntInRange(paramConfig.min, paramConfig.max + 1);
        } else {
          params[paramName] = randomInRange(paramConfig.min, paramConfig.max);
        }
      });
      let success = false;
      let retryConfig = MODIFIER_RETRY_CONFIG[type] || {};
      let maxTries = MODIFIER_RETRY_CONFIG.retryMaxCount || 10;
      let attemptedParams = [];
      if (RANDOM_LAMP_DEBUG) console.log(`\n[RandomLamp] Trying to enable modifier: ${type}`);
      for (let t = 0; t < maxTries; t++) {
        const currentEnabledUnordered = [
          ...enabledModifiers,
          {
            type,
            disabled: false,
            is_symmetrical: config.is_symmetrical,
            parameters: params
          }
        ];
        const currentEnabled = modifierTypes
          .map(mt => currentEnabledUnordered.find(m => m.type === mt))
          .filter(Boolean);
        const tempGeometry = new LampGeometry(
          {
            diameter: newDiameter,
            height: newHeight,
            tubularSegments: Math.max(4, Math.round(newHeight / TARGET_FACE_SIZE)),
            radialSegments: Math.max(8, Math.round(newDiameter * Math.PI / TARGET_FACE_SIZE)),
            profilePoints: newProfilePoints
          },
          currentEnabled,
          lampNoiseSeed
        );
        const threeGeometry = tempGeometry.getGeometry();
        const { hasFootprintClash, hasOverhang, hasPrintableAreaClash } = validateLampGeometry(
          threeGeometry,
          { overhangThreshold: angleThreshold, height: newHeight }
        );
        tempGeometry.dispose && tempGeometry.dispose();
        threeGeometry.dispose && threeGeometry.dispose();
        let errorCause = '';
        if (hasFootprintClash) errorCause += 'FootprintClash ';
        if (hasOverhang) errorCause += 'Overhang ';
        if (hasPrintableAreaClash) errorCause += 'PrintableAreaClash ';
        if (!errorCause) errorCause = 'None';
        const paramsBefore = { ...params };
        attemptedParams.push({
          attempt: t + 1,
          ...paramsBefore,
          error: errorCause.trim()
        });
        if (!hasFootprintClash && !hasOverhang && !hasPrintableAreaClash) {
          enabledModifiers = currentEnabled;
          appliedModifierOrder = enabledModifiers.map(m => m.type);
          success = true;
          break;
        }
        Object.entries(params).forEach(([paramName, value]) => {
          const reduction = retryConfig[paramName] || 0;
          if (typeof value === 'number' && reduction > 0) {
            params[paramName] = value * (1 - reduction / 100);
          }
        });
      }
      if (attemptedParams.length > 0 && RANDOM_LAMP_DEBUG) {
        console.log(`[RandomLamp]   Parameter attempts for modifier '${type}':`);
        console.log('appliedModifierOrder:', appliedModifierOrder);
        console.table(attemptedParams);
      }
      if (!success) {
        allModifiersValid = false;
        if (RANDOM_LAMP_DEBUG) console.log(`[RandomLamp]   Failed to enable modifier '${type}' after ${maxTries} attempts. Aborting this attempt.`);
        break;
      } else {
        if (RANDOM_LAMP_DEBUG) console.log(`[RandomLamp]   Enabled modifier '${type}' with params:`, params);
      }
    }

    if (!allModifiersValid || enabledModifiers.length !== numToApply) {
      if (RANDOM_LAMP_DEBUG) console.log('[RandomLamp] ❌ Could not build up valid modifier sequence, retrying...');
      continue;
    }
    const colorPair = generateAestheticCompliantColorPair();
    const newRenderingConfig = {
      ...defaultRenderingConfig,
      material: {
        ...defaultRenderingConfig.material,
        color: colorPair.lampColor,
        roughness: randomInRange(0.5, 1),
        metalness: 0,
        opacity: 0.95,
        emissiveColor: colorPair.emissiveColor,
        emissiveIntensity: randomInRange(0, 0.5)
      },
      background: {
        ...defaultRenderingConfig.background,
        color: colorPair.backgroundColor
      },
      view: {
        ...renderingConfig.view,
        wireframe: renderingConfig.view.wireframe,
        wireframeThickness: renderingConfig.view.wireframeThickness
      }
    };
    const finalEnabledModifiers = modifierTypes
      .map(mt => enabledModifiers.find(m => m.type === mt))
      .filter(Boolean);
    const tempGeometry = new LampGeometry(
      {
        diameter: newDiameter,
        height: newHeight,
        tubularSegments: Math.max(4, Math.round(newHeight / TARGET_FACE_SIZE)),
        radialSegments: Math.max(8, Math.round(newDiameter * Math.PI / TARGET_FACE_SIZE)),
        profilePoints: newProfilePoints
      },
      finalEnabledModifiers,
      lampNoiseSeed
    );
    const threeGeometry = tempGeometry.getGeometry();
    const { hasFootprintClash, hasOverhang, hasPrintableAreaClash } = validateLampGeometry(
      threeGeometry,
      { overhangThreshold: angleThreshold, height: newHeight }
    );
    tempGeometry.dispose && tempGeometry.dispose();
    threeGeometry.dispose && threeGeometry.dispose();
    if (RANDOM_LAMP_DEBUG) {
      console.log('[RandomLamp] Actual modifier application order:', finalEnabledModifiers.map(m => m.type));
      console.log('[RandomLamp] Final validation:', { hasFootprintClash, hasOverhang, hasPrintableAreaClash });
    }
    if (!hasFootprintClash && !hasOverhang && !hasPrintableAreaClash) {
      validDesign = true;
      if (RANDOM_LAMP_DEBUG) console.log('[RandomLamp] ✅ Valid design found on attempt', attempts);
      const validatedTubularSegments = Math.max(4, Math.round(newHeight / TARGET_FACE_SIZE));
      const validatedRadialSegments = Math.max(8, Math.round(newDiameter * Math.PI / TARGET_FACE_SIZE));
      const lampDesign = {
        baseShape: {
          diameter: newDiameter,
          height: newHeight,
          tubularSegments: validatedTubularSegments,
          radialSegments: validatedRadialSegments,
          profilePoints: newProfilePoints
        },
        modifiers: finalEnabledModifiers,
        rendering: newRenderingConfig,
        noiseSeed: lampNoiseSeed
      };
      return lampDesign;
    } else {
      if (RANDOM_LAMP_DEBUG) console.log('[RandomLamp] ❌ Final validation failed, retrying...');
    }
  }
  throw new Error('Unable to generate a valid random design after max attempts');
} 